import { SanityInstitution } from '@data/sanity/queries/types/institution'
import { SanitySiteStrings } from '@data/sanity/queries/types/strings'
import {
  AttributeInput,
  CartLineInput,
  CartSelectableAddressInput,
  DeliveryAddressValidationStrategy,
} from '@data/shopify/storefront/types'
import { LineItem, LineItemAttribute } from '@lib/cart'
import { compareNumbers } from '@lib/helpers'
import { defaultCountryCodes, Locale } from '@lib/language'
import { trimUnderscores } from '@lib/helpers'
import { ShopifyAddress } from './address'
import {
  assetFilenameAttributeKey,
  assetUrlAttributeKey,
  cartIdAttributeKey,
  configurationIdAttributeKey,
  imageAssetColorAttributeKey,
  imageAssetFileIdAttributeKey,
  imageAssetTitleAttributeKey,
  imageAssetUrlAttributeKey,
  institutionIdAttributeKey,
  logoAssetColorAttributeKey,
  logoAssetFileIdAttributeKey,
  logoAssetTitleAttributeKey,
  logoAssetUrlAttributeKey,
  partnerAdsClickId2AttributeKey,
  partnerAdsClickIdAttributeKey,
  partnerAdsIdAttributeKey,
  productNameAttributeKey,
  productVariantIdAttributeKey,
  shopIdAttributeKey,
  taxExemptAttributeKey,
  textAttributeKey,
  textColorAttributeKey,
  vatIdAttributeKey,
} from './cart'
import { ShopifyCustomer } from './customer'
import { getShopifyAssetUrl } from './file'
import { shippableProductTypes } from '@lib/sanity/product'

export interface ShopifyCheckoutNoteAttribute {
  name: string
  value: string
}

interface ShopifyCheckoutLineItemProperty {
  name: string
  value: string
}

interface ShopifyCheckoutLineItemUnitPriceMeasurement {
  measured_type?: string
  quantity_value?: string
  quantity_unit?: string
  reference_value?: string
  reference_unit?: string
}

interface ShopifyCheckoutLineItem {
  applied_discounts: unknown[]
  discount_allocations: unknown[]
  key: string
  destination_location_id?: number
  fulfillment_service: string
  gift_card: boolean
  grams: number
  origin_location_id: number
  presentment_title: string
  presentment_variant_title: string
  product_id: number
  properties?: ShopifyCheckoutLineItemProperty[]
  quantity: number
  requires_shipping: boolean
  sku: string
  tax_lines: unknown[]
  taxable: boolean
  title: string
  variant_id?: number
  variant_title?: string
  variant_price?: string
  vendor?: string
  user_id?: string
  unit_price_measurement?: ShopifyCheckoutLineItemUnitPriceMeasurement
  rank?: string
  compare_at_price?: string
  line_price: string
  price: string
}

export interface ShopifyCheckout {
  id: number
  token: string
  cart_token: string
  email?: string
  gateway?: string
  buyer_accepts_marketing: boolean
  created_at: string
  updated_at: string
  landing_site?: string
  note?: string
  note_attributes?: ShopifyCheckoutNoteAttribute[]
  referring_site?: string
  shipping_lines: unknown[]
  taxes_included: boolean
  total_weight: number
  currency: string
  completed_at?: string
  closed_at?: string
  user_id?: string
  location_id?: string
  source_identifier?: string
  source_url?: string
  device_id?: string
  phone?: string
  customer_locale?: string
  line_items: ShopifyCheckoutLineItem[]
  name: string
  source?: string
  abandoned_checkout_url: string
  discount_codes: unknown[]
  tax_lines: unknown[]
  source_name: string
  presentment_currency: string
  buyer_accepts_sms_marketing: boolean
  sms_marketing_phone?: string
  total_discounts: string
  total_line_items_price: string
  total_price: string
  total_tax: string
  subtotal_price: string
  total_duties?: string
  billing_address?: ShopifyAddress
  shipping_address?: ShopifyAddress
  customer?: ShopifyCustomer
}

/**
 * Gets cart attributes for checking out.
 */
export const getCartAttributesForCheckout = (cartId: string) => {
  const attributes: AttributeInput[] = [
    {
      key: `_${cartIdAttributeKey}`,
      value: cartId,
    },
  ]

  return attributes
}

/**
 * Gets checkout line item attribute names.
 */
const getCheckoutLineItemAttributeNames = (strings: SanitySiteStrings) => {
  const attributeNames: Record<string, string | undefined> = {
    // Private names
    [imageAssetUrlAttributeKey]: `_${assetUrlAttributeKey}`,
    [logoAssetUrlAttributeKey]: `_${assetUrlAttributeKey}`,
    // Public names
    [assetFilenameAttributeKey]: strings.cartAssetFilenameAttributeName,
    [textAttributeKey]: strings.cartFreetextAttributeName,
    [textColorAttributeKey]: strings.cartFreetextColorAttributeName,
    [imageAssetColorAttributeKey]: strings.cartAssetColorAttributeName,
    [imageAssetTitleAttributeKey]: strings.cartAssetTitleAttributeName,
    [logoAssetColorAttributeKey]: strings.cartAssetColorAttributeName,
    [logoAssetTitleAttributeKey]: strings.cartAssetTitleAttributeName,
    [productNameAttributeKey]: strings.cartProductNameAttributeName,
    [shopIdAttributeKey]: strings.cartShopIdAttributeName,
  }

  return attributeNames
}

/**
 * Gets product title for a fee line item.
 */
const getFeeItemProductTitle = (
  lineItems: LineItem[],
  attributes: LineItemAttribute[],
) => {
  const variantAttribute = attributes.find((attribute) => {
    const trimmedKey = trimUnderscores(attribute.key)

    return trimmedKey === productVariantIdAttributeKey
  })
  const variantId = variantAttribute?.value

  if (!variantId) {
    return null
  }

  const lineItem = lineItems.find((lineItem) => `${lineItem.id}` === variantId)

  if (!lineItem) {
    return null
  }

  return lineItem.product?.title
}

/**
 * Convert asset file ID attributes into asset URL attributes.
 */
const convertFileIdAttributes = async (
  locale: Locale,
  attributes: LineItemAttribute[],
) => {
  let convertedAttributes = [...attributes]

  // Find logo file ID
  const logoFileIdAttribute = convertedAttributes.find((attribute) => {
    return [
      logoAssetFileIdAttributeKey,
      `_${logoAssetFileIdAttributeKey}`,
    ].includes(attribute.key)
  })
  const logoFileId = logoFileIdAttribute?.value
  const logoAssetUrl = logoFileId
    ? await getShopifyAssetUrl(locale, logoFileId)
    : null

  if (logoAssetUrl) {
    // Create or overwrite logo URL attribute
    convertedAttributes = convertedAttributes.filter((attribute) => {
      return ![
        logoAssetUrlAttributeKey,
        `_${logoAssetUrlAttributeKey}`,
      ].includes(attribute.key)
    })
    convertedAttributes.push({
      key: `_${logoAssetUrlAttributeKey}`,
      value: logoAssetUrl,
    })
  }

  // Find image file ID
  const imageFileIdAttribute = convertedAttributes.find((attribute) => {
    return [
      imageAssetFileIdAttributeKey,
      `_${imageAssetFileIdAttributeKey}`,
    ].includes(attribute.key)
  })
  const imageFileId = imageFileIdAttribute?.value
  const imageAssetUrl = imageFileId
    ? await getShopifyAssetUrl(locale, imageFileId)
    : null

  if (imageAssetUrl) {
    // Create or overwrite image URL attribute
    convertedAttributes = convertedAttributes.filter((attribute) => {
      return ![
        imageAssetUrlAttributeKey,
        `_${imageAssetUrlAttributeKey}`,
      ].includes(attribute.key)
    })
    convertedAttributes.push({
      key: `_${imageAssetUrlAttributeKey}`,
      value: imageAssetUrl,
    })
  }

  return convertedAttributes
}

/**
 * Gets product name line item attribute.
 */
const getProductNameAttribute = (
  lineItems: LineItem[],
  attributes: LineItemAttribute[],
) => {
  const productTitle = getFeeItemProductTitle(lineItems, attributes)

  if (!productTitle) {
    return null
  }

  const attributeInput: AttributeInput = {
    key: `_${productNameAttributeKey}`,
    value: productTitle,
  }

  return attributeInput
}

/**
 * Parses cart line item attributes for checking out.
 */
const parseCartLineItemAttributesForCheckout = async (
  locale: Locale,
  strings: SanitySiteStrings,
  lineItems: LineItem[],
  attributes?: LineItemAttribute[],
) => {
  if (!attributes) {
    return
  }

  // Convert file ID attributes to file URL attributes
  const convertedAttributes = await convertFileIdAttributes(locale, attributes)
  const feeItemAttrbiutes = [
    assetFilenameAttributeKey,
    textAttributeKey,
    textColorAttributeKey,
    imageAssetColorAttributeKey,
    imageAssetTitleAttributeKey,
    logoAssetColorAttributeKey,
    logoAssetTitleAttributeKey,
  ]

  const isFeeProduct = convertedAttributes.some((attribute) => {
    const trimmedKey = trimUnderscores(attribute.key)

    return (feeItemAttrbiutes as string[]).includes(trimmedKey)
  })

  const productNameAttribute = isFeeProduct
    ? getProductNameAttribute(lineItems, convertedAttributes)
    : null

  // Filter cart line item attributes
  const checkoutPublicLineItemAttributeWhitelist = [
    // All attributes that have a public name
    assetFilenameAttributeKey,
    textAttributeKey,
    textColorAttributeKey,
    imageAssetColorAttributeKey,
    imageAssetTitleAttributeKey,
    logoAssetColorAttributeKey,
    logoAssetTitleAttributeKey,
    productNameAttributeKey,
    shopIdAttributeKey,
  ]
  const checkoutPrivateFeeLineItemAttributeBlacklist = [
    institutionIdAttributeKey,
    shopIdAttributeKey,
  ]
  const checkoutPrivateLineItemAttributeWhitelist = [
    configurationIdAttributeKey,
    // Carrier service shipping information
    institutionIdAttributeKey,
    // Fee product URLs
    imageAssetUrlAttributeKey,
    logoAssetUrlAttributeKey,
    // Fee product linking to print products for packing slip
    productVariantIdAttributeKey,
    // Customer type information
    taxExemptAttributeKey,
    vatIdAttributeKey,
    // PartnerAds data
    partnerAdsClickIdAttributeKey,
    partnerAdsClickId2AttributeKey,
    partnerAdsIdAttributeKey,
  ]
  let filteredAttributes = convertedAttributes
    .map((attribute) => {
      const attributeInput: AttributeInput = {
        key: attribute.key,
        value: attribute.value,
      }

      return attributeInput
    })
    .filter((attribute) => {
      const trimmedKey = trimUnderscores(attribute.key)

      return (
        !isFeeProduct ||
        !(checkoutPrivateFeeLineItemAttributeBlacklist as string[]).includes(
          trimmedKey,
        )
      )
    })
    .filter((attribute) => {
      const trimmedKey = trimUnderscores(attribute.key)

      return (
        (checkoutPublicLineItemAttributeWhitelist as string[]).includes(
          trimmedKey,
        ) ||
        (checkoutPrivateLineItemAttributeWhitelist as string[]).includes(
          trimmedKey,
        )
      )
    })

  if (productNameAttribute) {
    filteredAttributes = [productNameAttribute, ...filteredAttributes]
  }

  // Convert cart line item attributes to checkout line iten attributes
  const checkoutLineItemAttributes = filteredAttributes.map((attribute) => {
    // Get attribute name from key
    const trimmedKey = trimUnderscores(attribute.key)
    const attributeNames = getCheckoutLineItemAttributeNames(strings)
    const attributeName = attributeNames[trimmedKey]

    const attributeInput: AttributeInput = {
      key: attributeName ?? attribute.key,
      value: attribute.value,
    }

    return attributeInput
  })

  return checkoutLineItemAttributes.sort((attribute1, attribute2) => {
    return compareNumbers(
      attribute1.key[0] === '_' ? 1 : 0,
      attribute2.key[0] === '_' ? 1 : 0,
    )
  })
}

/**
 * Parses cart line items for checking out.
 */
export const parseCartLineItemsForCheckout = async (
  locale: Locale,
  strings: SanitySiteStrings,
  lineItems: LineItem[],
) => {
  return await Promise.all(
    lineItems.map(async (lineItem) => {
      const cartLineInput: CartLineInput = {
        merchandiseId: lineItem.merchandiseId,
        quantity: lineItem.quantity,
        attributes: await parseCartLineItemAttributesForCheckout(
          locale,
          strings,
          lineItems,
          lineItem.attributes,
        ),
      }

      return cartLineInput
    }),
  )
}

/**
 * Gets note text for cart.
 */
export const getCartNoteForCheckout = (
  strings: SanitySiteStrings,
  comment: string,
  institution: SanityInstitution | null,
) => {
  const noteLines: string[] = []

  if (comment) {
    noteLines.push(strings.cartCommentLabel)
    noteLines.push(comment)
  }

  if (institution) {
    noteLines.push(strings.cartSchoolLabel)
    noteLines.push(institution.name)
    noteLines.push(institution.address)
    noteLines.push(institution.city)
    noteLines.push(`${institution.postalCode}`)
  }

  return noteLines.join('\n')
}

/**
 * Gets delivery address input for cart.
 */
export const getCartDeliveryAddressForCheckout = (
  locale: Locale,
  lineItems: LineItem[],
  institutionPickup: boolean,
  institution: SanityInstitution | null,
) => {
  const hasShippableProducts = lineItems.some((lineItem) => {
    return (
      lineItem.product && shippableProductTypes.includes(lineItem.product.type)
    )
  })

  // Check if cart has any shippable products, institution pickup is enabled and institution is found
  if (!hasShippableProducts || !institutionPickup || !institution) {
    return null
  }

  const address: CartSelectableAddressInput = {
    address: {
      deliveryAddress: {
        address1: institution.address,
        company: institution.name,
        city: institution.city,
        countryCode: defaultCountryCodes[locale],
        zip: `${institution.postalCode}`,
      },
    },
    selected: true,
    validationStrategy: DeliveryAddressValidationStrategy.CountryCodeOnly,
  }

  return address
}
